import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import { AdminVideoRepository } from "../../../core/repository";

const PAGE_SIZE = 20;

const extractVideoData = (response) => {
  if (!response || response.error) {
    return { items: [], total: 0, lastPage: 1, currentPage: 1 };
  }

  const payloadData = response.data !== undefined ? response.data : response;

  if (Array.isArray(payloadData)) {
    return {
      items: payloadData,
      total: payloadData.length,
      lastPage: 1,
      currentPage: 1,
    };
  }

  if (payloadData && typeof payloadData === "object") {
    const items = Array.isArray(payloadData.data) ? payloadData.data : [];
    const total = payloadData.total !== undefined ? Number(payloadData.total) : items.length;
    const lastPage =
      payloadData.last_page !== undefined
        ? Number(payloadData.last_page)
        : Math.ceil(total / (payloadData.per_page || PAGE_SIZE)) || 1;
    const currentPage =
      payloadData.current_page !== undefined ? Number(payloadData.current_page) : 1;

    return {
      items,
      total,
      lastPage,
      currentPage,
    };
  }

  return { items: [], total: 0, lastPage: 1, currentPage: 1 };
};

const DropdownContext = React.createContext({});

const CustomPaper = React.forwardRef((props, ref) => {
  const { loadingMore } = React.useContext(DropdownContext);
  const { children, ...paperProps } = props;

  return (
    <Paper
      {...paperProps}
      ref={ref}
      elevation={6}
      sx={{
        borderRadius: "10px",
        mt: 0.5,
        overflow: "hidden",
        ...paperProps.sx,
      }}
    >
      {children}
      {loadingMore && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 1.2,
            gap: 1.5,
            bgcolor: "#fafbfc",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <CircularProgress size={16} color="primary" />
          <Typography variant="body2" sx={{ fontSize: "13px", color: "#555", fontWeight: 500 }}>
            Loading more content...
          </Typography>
        </Box>
      )}
    </Paper>
  );
});

const VideoDropDownComponent = ({
  onchange,
  defaultValue,
  list = [],
  selectedValue,
  dropdownLabel = "Video",
  placeholder = "Search or select a video...",
  readOnly = false,
  isError = false,
  error = "",
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [open, setOpen] = useState(false);

  const searchTimerRef = useRef(null);
  const isFetchingRef = useRef(false);
  const scrollPosRef = useRef(0);
  const listboxRef = useRef(null);

  const fetchVideos = useCallback(
    async (pageNum = 1, search = "", isAppend = false) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const response = await AdminVideoRepository.fetchVideo({
          page: pageNum,
          per_page: PAGE_SIZE,
          ...(search && search.trim() ? { search: search.trim() } : {}),
        });

        const { items, lastPage, currentPage } = extractVideoData(response);

        setPage(currentPage);
        setHasMore(currentPage < lastPage);

        if (isAppend) {
          setOptions((prev) => {
            const existingIds = new Set(
              prev.map((item) => String(item.id || item.link))
            );
            const uniqueNew = items.filter(
              (item) => !existingIds.has(String(item.id || item.link))
            );
            return [...prev, ...uniqueNew];
          });
        } else {
          setOptions(items);
        }
      } catch (err) {
        console.error("Error fetching videos in VideoDropDown:", err);
      } finally {
        isFetchingRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  const hasUserSearchedRef = useRef(false);

  // Initialize options from props `list`
  useEffect(() => {
    if (Array.isArray(list) && list.length > 0) {
      if (!hasUserSearchedRef.current) {
        setOptions(list);
      }
    }
  }, [list]);

  // Maintain scroll position when new paginated items are appended
  useEffect(() => {
    if (listboxRef.current && scrollPosRef.current > 0) {
      listboxRef.current.scrollTop = scrollPosRef.current;
    }
  }, [options]);

  // Lazy load initial page only when dropdown opens if options are completely empty and user hasn't searched
  const handleOpen = () => {
    setOpen(true);
    if (options.length === 0 && (!list || list.length === 0) && !hasUserSearchedRef.current) {
      fetchVideos(1, "", false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    scrollPosRef.current = 0;
  };

  // "Search as you type" ONLY when the user explicitly types (reason === 'input')
  const handleSearchChange = (event, newInputValue, reason) => {
    if (reason === "input") {
      hasUserSearchedRef.current = true;
      setSearchQuery(newInputValue);
      scrollPosRef.current = 0;
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
      searchTimerRef.current = setTimeout(() => {
        setPage(1);
        setHasMore(true);
        fetchVideos(1, newInputValue, false);
      }, 350);
    } else if (reason === "clear") {
      hasUserSearchedRef.current = false;
      setSearchQuery("");
      scrollPosRef.current = 0;
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
      if (Array.isArray(list) && list.length > 0) {
        setOptions(list);
      } else {
        setPage(1);
        setHasMore(true);
        fetchVideos(1, "", false);
      }
    }
  };

  // "Load on scroll with pagination"
  const handleListboxScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (!listboxNode) return;

    scrollPosRef.current = listboxNode.scrollTop;

    const { scrollTop, scrollHeight, clientHeight } = listboxNode;
    if (scrollTop + clientHeight >= scrollHeight - 40) {
      if (!isFetchingRef.current && !loadingMore && !loading && hasMore) {
        const nextPage = page + 1;
        fetchVideos(nextPage, searchQuery, true);
      }
    }
  };

  // Resolve selected video object from selectedValue
  const selectedOption = useMemo(() => {
    if (!selectedValue && selectedValue !== 0) return null;
    const strVal = String(selectedValue);
    const match = options.find(
      (opt) => String(opt?.link) === strVal || String(opt?.id) === strVal
    );
    if (match) return match;

    if (Array.isArray(list) && list.length > 0) {
      const listMatch = list.find(
        (opt) => String(opt?.link) === strVal || String(opt?.id) === strVal
      );
      if (listMatch) return listMatch;
    }

    if (typeof selectedValue === "object" && selectedValue !== null) {
      return selectedValue;
    }

    return {
      link: selectedValue,
      title: String(selectedValue),
      id: selectedValue,
    };
  }, [selectedValue, options, list]);

  const handleChange = (event, newValue) => {
    if (onchange) {
      const val = newValue?.link || (typeof newValue === "string" ? newValue : "");
      const syntheticEvent = {
        target: {
          value: val,
          name: dropdownLabel || "video",
        },
      };
      onchange(syntheticEvent, newValue);
    }
  };

  return (
    <DropdownContext.Provider value={{ loadingMore }}>
      <Box sx={{ width: "100%", padding: "10px" }}>
        {dropdownLabel ? (
          <p
            style={{
              padding: 0,
              margin: 0,
              marginBottom: 10,
              color: "#4E657C",
              fontSize: 19,
              fontWeight: 500,
            }}
          >
            {dropdownLabel}
          </p>
        ) : null}

        <Autocomplete
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          disabled={readOnly}
          options={options}
          value={selectedOption}
          filterOptions={(options) => options}
          autoHighlight={false}
          disableListWrap
          getOptionLabel={(option) => {
            if (!option) return "";
            if (typeof option === "string") return option;
            return option?.title || option?.name || option?.link || "";
          }}
          isOptionEqualToValue={(option, value) => {
            if (!option || !value) return false;
            const optVal = String(option?.link || option?.id || "");
            const valVal = typeof value === "object" ? String(value?.link || value?.id || "") : String(value);
            return optVal === valVal;
          }}
          onInputChange={handleSearchChange}
          onChange={handleChange}
          loading={loading || loadingMore}
          noOptionsText={loading ? "Searching videos..." : "No videos found"}
          PaperComponent={CustomPaper}
          ListboxProps={{
            onScroll: handleListboxScroll,
            ref: (node) => {
              if (node) {
                listboxRef.current = node;
                if (scrollPosRef.current > 0) {
                  node.scrollTop = scrollPosRef.current;
                }
              }
            },
            sx: {
              maxHeight: 280,
              "& .MuiAutocomplete-option": {
                fontSize: "14px",
                padding: "10px 14px",
              },
            },
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id ? `video-${option.id}` : `link-${option.link}`}>
              <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: "#2D3748",
                    wordBreak: "break-word",
                  }}
                >
                  {option.title || option.name || option.link}
                </Typography>
                {option.duration ? (
                  <Typography variant="caption" sx={{ color: "#718096", mt: 0.25 }}>
                    Duration: {option.duration} min
                  </Typography>
                ) : null}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              size="small"
              error={isError}
              helperText={isError ? error : ""}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  minHeight: "44px",
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading || loadingMore ? (
                      <CircularProgress color="primary" size={18} sx={{ mr: 1 }} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
    </DropdownContext.Provider>
  );
};

export default VideoDropDownComponent;